import { useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Field";
import { DEFAULT_DELIVERABLES, rateForRole, SIGN_BY_DAYS } from "@/lib/crew";
import { daysUntil, formatAgo, type IsoDate } from "@/lib/dates";
import { TEAM_CONFIRM_DAYS, TEAM_ROLE_LABEL, type TeamRole } from "@/lib/jobs";
import { bookingState, CrewBooking, seatBadge } from "./CrewBooking";
import {
  useConfirmTeam,
  useJobTeam,
  useRemoveSeat,
  useUpdateSeat,
  useSaveSeat,
  useTeamMembers,
  type JobSeat,
} from "./queries";

const DEFAULT_SEATS: TeamRole[] = [
  "lead_photographer",
  "lead_videographer",
  "second_photographer",
];

/**
 * Who is on the day, as on her Trello cards: "Main photographer: Jenalee,
 * Main videographer: Henk, Second photographer: TBD". An empty seat is "to be
 * decided". The contract promises the couple their team 4–6 weeks before.
 *
 * Picking a name only saves it (27 Oct 2026); each seat shows whether its
 * agreement is sent, and "Send agreement" opens the booking with the Gmail
 * message ready.
 */
export function JobTeam({
  jobId,
  jobTitle,
  venue,
  eventDate,
  confirmedAt,
}: {
  jobId: string;
  jobTitle: string;
  venue: string | null;
  eventDate: string;
  confirmedAt: string | null;
}) {
  const seats = useJobTeam(jobId);
  const members = useTeamMembers();
  const save = useSaveSeat(jobId);
  const remove = useRemoveSeat(jobId);
  const confirm = useConfirmTeam(jobId);
  const [openSeat, setOpenSeat] = useState<string | null>(null);

  const update = useUpdateSeat(jobId);
  // Released seats are the record of a booking that ended; they live on the
  // person's page, not in the line-up.
  const list = (seats.data ?? []).filter((seat) => seat.status !== "released");

  function releaseSeat(seat: JobSeat, reason: string) {
    return update.mutateAsync({
      id: seat.id,
      status: "released",
      released_at: new Date().toISOString(),
      released_by: "us",
      released_reason: reason,
    });
  }
  const days = daysUntil(eventDate);
  const dueToConfirm = !confirmedAt && days >= 0 && days <= TEAM_CONFIRM_DAYS;
  const memberOf = (id: string | null) =>
    (members.data ?? []).find((member) => member.id === id);
  const unsigned = list.filter((seat) => {
    const state = bookingState(seat, memberOf(seat.member_id));
    return state === "empty" || state === "draft" || state === "sent";
  });
  const signByWarning =
    days >= 0 && days <= SIGN_BY_DAYS && unsigned.length > 0;

  function addDefaults() {
    DEFAULT_SEATS.forEach((role, index) =>
      save.mutate({ role, member_id: null, position: index }),
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {seats.isPending ? (
        <p className="text-[length:var(--text-2xs)] text-taupe">
          Loading the team…
        </p>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[length:var(--text-sm)] text-taupe">
            Nobody assigned yet.
          </p>
          <Button size="sm" onClick={addDefaults}>
            Add photographer, videographer and second shooter
          </Button>
        </div>
      ) : (
        <ul className="flex flex-col">
          {list.map((seat) => {
            const member = memberOf(seat.member_id);
            const state = bookingState(seat, member);
            const badge = seatBadge(seat, state);
            // Nothing locks (28 Oct 2026): a seat with an agreement sent or
            // signed can still change role, person or be removed. The signed
            // agreement is never rewritten: changing the person or removing
            // the seat releases it, so it stays on that person's page.
            const booked = state !== "self" && seat.status !== "draft";
            return (
              <li key={seat.id} className="border-b py-2.5 last:border-b-0">
                <div className="grid items-center gap-3 sm:grid-cols-2 2xl:grid-cols-[12rem_minmax(0,1fr)_auto]">
                  <Select
                    aria-label="Role"
                    value={seat.role}
                    onChange={(event) =>
                      save.mutate({
                        id: seat.id,
                        role: event.target.value as TeamRole,
                        member_id: seat.member_id,
                      })
                    }
                  >
                    {(Object.keys(TEAM_ROLE_LABEL) as TeamRole[]).map(
                      (role) => (
                        <option key={role} value={role}>
                          {TEAM_ROLE_LABEL[role]}
                        </option>
                      ),
                    )}
                  </Select>
                  <Select
                    aria-label="Who"
                    value={seat.member_id ?? ""}
                    onChange={(event) => {
                      const member_id = event.target.value || null;
                      const picked = memberOf(member_id);
                      const rate = picked
                        ? rateForRole(
                            {
                              lead_rate:
                                picked.lead_rate === null
                                  ? null
                                  : Number(picked.lead_rate),
                              second_rate:
                                picked.second_rate === null
                                  ? null
                                  : Number(picked.second_rate),
                            },
                            seat.role,
                          )
                        : null;
                      if (booked) {
                        if (
                          !window.confirm(
                            `${member?.name ?? "This person"}'s agreement for this project was ${seat.status === "signed" ? "signed" : "sent"}. Swap to ${picked?.name ?? "to be decided"}? Their agreement is kept on their page, marked released.`,
                          )
                        ) {
                          event.target.value = seat.member_id ?? "";
                          return;
                        }
                        void releaseSeat(seat, `Swapped to ${picked?.name ?? "someone else"}`).then(() =>
                          save.mutate({
                            role: seat.role,
                            member_id,
                            position: seat.position,
                            deliverables: member_id ? DEFAULT_DELIVERABLES[seat.role] : [],
                            fee: rate,
                          }),
                        );
                        return;
                      }
                      save.mutate({
                        id: seat.id,
                        role: seat.role,
                        member_id,
                        ...(member_id && seat.deliverables.length === 0
                          ? { deliverables: DEFAULT_DELIVERABLES[seat.role] }
                          : {}),
                        ...(seat.fee === null && rate !== null
                          ? { fee: rate }
                          : {}),
                      });
                    }}
                  >
                    <option value="">To be decided</option>
                    {(members.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                        {m.book_again === "no"
                          ? " (you said: don't book again)"
                          : ""}
                      </option>
                    ))}
                  </Select>
                  <div className="flex flex-wrap items-center gap-1 sm:col-span-2 2xl:col-span-1">
                    {badge ? (
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                    ) : null}
                    {member && state !== "self" ? (
                      <Button
                        variant={state === "draft" && openSeat !== seat.id ? "primary" : "secondary"}
                        size="sm"
                        onClick={() =>
                          setOpenSeat(openSeat === seat.id ? null : seat.id)
                        }
                      >
                        {openSeat === seat.id
                          ? "Close"
                          : state === "draft"
                            ? "Send agreement"
                            : "Booking details"}
                      </Button>
                    ) : null}
                    {/* Every seat can be removed (28 Oct 2026); one with an
                        agreement sent or signed asks first and is released,
                        not deleted. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remove this seat"
                      onClick={() => {
                        if (!booked) {
                          remove.mutate(seat.id);
                          return;
                        }
                        if (
                          window.confirm(
                            `Remove ${member?.name ?? "this person"} from this project? Their ${seat.status === "signed" ? "signed" : "sent"} agreement is kept on their page, marked released.`,
                          )
                        )
                          void releaseSeat(seat, "Removed from this project");
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
                {member?.book_again === "no" && seat.status === "draft" ? (
                  <p className="pt-1 text-[length:var(--text-2xs)] text-clay">
                    You marked {member.name} as someone you wouldn't book again.{" "}
                    <Link
                      to={`/team/${member.id}`}
                      className="underline underline-offset-4"
                    >
                      See why
                    </Link>
                  </p>
                ) : null}
                {openSeat === seat.id && member && state !== "self" ? (
                  <CrewBooking
                    key={`${seat.id}-${seat.updated_at}`}
                    jobId={jobId}
                    jobTitle={jobTitle}
                    eventDate={eventDate as IsoDate}
                    venue={venue}
                    seat={seat}
                    member={member}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {list.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2.5"
            onClick={() =>
              save.mutate({
                role: "second_photographer",
                member_id: null,
                position: list.length,
              })
            }
          >
            Add a seat
          </Button>
          {confirmedAt ? (
            <span className="text-[length:var(--text-2xs)] text-sage">
              Team confirmed to the couple {formatAgo(confirmedAt)}.{" "}
              <button
                type="button"
                className="underline underline-offset-4"
                onClick={() => confirm.mutate(false)}
              >
                Undo
              </button>
            </span>
          ) : (
            <Button
              size="sm"
              variant={dueToConfirm ? "primary" : "secondary"}
              onClick={() => confirm.mutate(true)}
            >
              Team confirmed to the couple
            </Button>
          )}
        </div>
      ) : null}

      {signByWarning ? (
        <p className="text-[length:var(--text-2xs)] text-amber">
          {unsigned.length === 1
            ? "One seat is"
            : `${unsigned.length} seats are`}{" "}
          not booked in writing yet, {days} days out. Nobody is booked until
          they have signed.
        </p>
      ) : null}

      {dueToConfirm ? (
        <p className="text-[length:var(--text-2xs)] text-amber">
          The wedding is {days} days away: the contract promises the couple
          their team 4–6 weeks before.
        </p>
      ) : null}
    </div>
  );
}
